import { useState, useEffect } from "react";
import { puedeCrear } from "../auth/permisos";

import { avisarError } from "../../shared/errores";
import { eliminar as eliminarFila, guardar as guardarFila, listar } from "../../services/datos";
import { avisar, confirmar } from "../../shared/avisos";
import Esqueleto from "../../shared/components/Esqueleto";

const camposPorDefecto = {
  apellido: "",
  direccion: "",
  barrio: "",
  ciudad: "",
  telefono: "",
  notas: ""
};

function FormFamilia({ inicial, onGuardar, onCancelar }) {
  const [form, setForm] = useState(inicial ? { ...inicial } : { ...camposPorDefecto });
  const input = "campo";
  const label = "etiqueta";

  useEffect(() => {
    if (inicial) {
      setForm({ ...inicial });
    } else {
      setForm({ ...camposPorDefecto });
    }
  }, [inicial]);

  const guardar = () => {
    if (!form.apellido.trim()) {
      avisar("Apellido es obligatorio");
      return;
    }
    onGuardar(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm ventana-fondo">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-zinc-950 p-6 sm:p-8 ventana-panel">
        <h3 className="titulo-ventana mb-5">{inicial ? "Editar Familia" : "Nueva Familia"}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={label}>Apellido *</label>
            <input className={input} value={form.apellido} onChange={(e) => setForm({ ...form, apellido: e.target.value })} placeholder="Apellido de la familia" />
          </div>
          <div>
            <label className={label}>Ciudad</label>
            <input className={input} value={form.ciudad} onChange={(e) => setForm({ ...form, ciudad: e.target.value })} placeholder="Ciudad" />
          </div>
          <div>
            <label className={label}>Barrio</label>
            <input className={input} value={form.barrio} onChange={(e) => setForm({ ...form, barrio: e.target.value })} placeholder="Barrio" />
          </div>
          <div>
            <label className={label}>Dirección</label>
            <input className={input} value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} placeholder="Dirección" />
          </div>
          <div className="md:col-span-2">
            <label className={label}>Teléfono</label>
            <input className={input} value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="Teléfono" />
          </div>
          <div className="md:col-span-2">
            <label className={label}>Notas</label>
            <textarea className={input} rows={4} value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} placeholder="Comentarios adicionales" />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={guardar} className="flex-1 btn-primario">Guardar</button>
          <button onClick={onCancelar} className="flex-1 btn-fantasma">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function Familias({ usuario }) {
  const [familias, setFamilias] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [buscar, setBuscar] = useState("");
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editar, setEditar] = useState(null);

  const cargar = () => {
    setCargando(true);
    listar("familias", { orden: "apellido" }).then((d) => { setFamilias(d); setCargando(false); }).catch((e) => { setCargando(false); avisarError(e); });
  };

  useEffect(() => { cargar(); }, []);

  const guardar = (form) => {
    guardarFila("familias", editar?.id, form)
      .then(() => { setMostrarForm(false); setEditar(null); cargar(); })
      .catch((e) => avisar(e.message, "error"));
  };

  const eliminar = async (id, apellido) => {
    if (!(await confirmar(`¿Eliminar familia ${apellido}?`, { aceptar: "Eliminar" }))) return;
    eliminarFila("familias", id).then(() => cargar()).catch((e) => avisar(e.message, "error"));
  };

  const familiasFiltradas = familias.filter((f) =>
    `${f.apellido} ${f.ciudad || ""} ${f.barrio || ""}`.toLowerCase().includes(buscar.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between mb-6">
        <div>
          <h2 className="titulo-pagina">Familias</h2>
          <p className="text-gray-400 text-sm mt-1">Administración de familias</p>
        </div>
        {puedeCrear(usuario) ? (
          <button onClick={() => { setEditar(null); setMostrarForm(true); }} className="btn-primario">Nueva Familia</button>
        ) : (
          <span className="text-sm text-gray-400">Solo pastor o administrador puede crear</span>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[280px_minmax(0,1fr)] gap-6 mb-6">
        <div className="panel p-4">
          <p className="text-gray-400 text-sm">Familias registradas</p>
          <p className="text-white font-black text-4xl mt-2">{familias.length}</p>
          <input
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            placeholder="Buscar por apellido, ciudad o barrio"
            className="mt-4 campo"
          />
        </div>

        <div className="panel p-4 max-h-[72vh] overflow-y-auto">
          {cargando ? (
            <Esqueleto texto="Cargando familias…" />
          ) : familiasFiltradas.length === 0 ? (
            <p className="text-gray-400">No se encontraron familias.</p>
          ) : (
            <div className="space-y-3">
              {familiasFiltradas.map((f) => (
                <div key={f.id} className="rounded-2xl border border-gray-700 p-4 bg-oscuro">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-white text-lg font-semibold">{f.apellido}</p>
                      <p className="text-gray-400 text-sm">{[f.direccion, f.barrio, f.ciudad].filter(Boolean).join(" · ")}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditar(f); setMostrarForm(true); }} className="btn-fantasma btn-sm">Editar</button>
                      <button onClick={() => eliminar(f.id, f.apellido)} className="btn-peligro btn-sm">Eliminar</button>
                    </div>
                  </div>
                  {f.telefono && <p className="text-gray-400 text-sm mt-3">Tel. {f.telefono}</p>}
                  {f.notas && <p className="text-gray-500 text-sm mt-2">{f.notas}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {mostrarForm && <FormFamilia inicial={editar} onGuardar={guardar} onCancelar={() => { setMostrarForm(false); setEditar(null); }} />}
    </div>
  );
}

export default Familias;
